/**
 * Persistencia local del trabajo pendiente de pedidos mobile.
 *
 * Se mantiene separado de la cola de pedidos: un borrador protege el trabajo
 * antes de confirmar, mientras que la cola protege pedidos ya confirmados.
 */

const DB_NAME = 'mobile_order_drafts_db'
const DB_VERSION = 1
const STORE_DRAFTS = 'drafts'
let dbPromise = null
let writeChain = Promise.resolve()

function openDb() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB no disponible'))
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = event => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        const store = db.createObjectStore(STORE_DRAFTS, { keyPath: 'id' })
        store.createIndex('sellerId', 'sellerId', { unique: false })
        store.createIndex('savedAt', 'savedAt', { unique: false })
      }
    }
    request.onsuccess = event => resolve(event.target.result)
    request.onerror = () => reject(request.error || new Error('No se pudo abrir IndexedDB'))
  })

  return dbPromise
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('Operación IndexedDB fallida'))
  })
}

function transactionToPromise(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error || new Error('Transacción IndexedDB fallida'))
    transaction.onabort = () => reject(transaction.error || new Error('Transacción IndexedDB abortada'))
  })
}

async function getSnapshotsForSeller(sellerId) {
  const db = await openDb()
  const store = db.transaction(STORE_DRAFTS, 'readonly').objectStore(STORE_DRAFTS)
  const index = store.index('sellerId')
  const snapshots = await requestToPromise(index.getAll(sellerId))
  return (snapshots || []).sort((a, b) => b.savedAt - a.savedAt)
}

function serializeSnapshot(snapshot) {
  return JSON.parse(JSON.stringify(snapshot))
}

function enqueueWrite(task) {
  writeChain = writeChain.catch(() => {}).then(task)
  return writeChain
}

const draftStorage = {
  list(sellerId) {
    if (!sellerId) return Promise.resolve([])
    return getSnapshotsForSeller(sellerId)
      .then(snapshots => snapshots.slice(0, 1))
      .catch(() => [])
  },

  save(sellerId, snapshot) {
    if (!sellerId) return Promise.resolve(null)

    // Serializar antes de encolar la escritura: representa el estado exacto
    // al momento de la acción, no una referencia reactiva viva.
    let data
    try {
      data = serializeSnapshot(snapshot)
    } catch (error) {
      return Promise.reject(new Error('No se pudo serializar el borrador'))
    }

    const task = async () => {
      const normalizedSellerId = parseInt(sellerId, 10)
      const record = {
        // Una fila fija por vendedor: sobrescribirla evita crear/borrar
        // registros completos en cada pulsación o cambio del carrito.
        id: `seller_${normalizedSellerId}`,
        sellerId: normalizedSellerId,
        savedAt: Date.now(),
        data,
      }
      const db = await openDb()
      const writeTransaction = db.transaction(STORE_DRAFTS, 'readwrite')
      const store = writeTransaction.objectStore(STORE_DRAFTS)
      store.put(record)
      await transactionToPromise(writeTransaction)

      // Limpieza única de snapshots creados por la versión anterior.
      const snapshots = await getSnapshotsForSeller(record.sellerId)
      const stale = snapshots.filter(item => item.id !== record.id)
      if (stale.length) {
        const cleanupTransaction = db.transaction(STORE_DRAFTS, 'readwrite')
        const cleanup = cleanupTransaction.objectStore(STORE_DRAFTS)
        stale.forEach(item => cleanup.delete(item.id))
        await transactionToPromise(cleanupTransaction)
      }
      return record
    }

    return enqueueWrite(task)
  },

  clearSeller(sellerId) {
    if (!sellerId) return false
    return enqueueWrite(async () => {
      const snapshots = await getSnapshotsForSeller(sellerId)
      const db = await openDb()
      const transaction = db.transaction(STORE_DRAFTS, 'readwrite')
      const store = transaction.objectStore(STORE_DRAFTS)
      snapshots.forEach(item => store.delete(item.id))
      await transactionToPromise(transaction)
      return true
    }).catch(() => {
      return false
    })
  },
}

export default draftStorage
